import { ref, watch, type Ref } from "vue";
import { debounce as debounceFn } from "@/utils/debounce";
import { useSchoolStore } from "@/stores/school";
import { useAuthenticationStore } from "@/stores/authentication";

export function asyncComputed<T>(
  func: (controller: AbortController, reload: boolean) => Promise<T>,
  baseWatchers: any[] = [],
  debounce?: number,
  resetOnLoad = true,
  defVal: any = undefined,
  reloadOnSchoolId = true
) {
  const value = ref<T>(defVal) as Ref<T>;
  const loading = ref(true);
  const schoolStore = useSchoolStore();
  const authStore = useAuthenticationStore();
  const shouldReloadOnSchoolId = reloadOnSchoolId && authStore.jwtData.authentication_type === "district";

  let controller: AbortController | undefined;
  let gLoadTime = 0;

  const load = debounce
    ? debounceFn(() => triggerLoad(false, resetOnLoad), debounce)
    : () => triggerLoad(false, resetOnLoad);

  function triggerLoad(reload = false, reset = resetOnLoad) {
    if (shouldReloadOnSchoolId && !schoolStore.school_id) return;
    const loadTime = (gLoadTime = Date.now());

    if (controller) controller.abort("reload");
    controller = new AbortController();

    loading.value = true;
    if (reset) value.value = defVal;

   func(controller, reload)
      .then((res) => {
        if (loadTime === gLoadTime) {
          value.value = res;
          loading.value = false;
        }
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          console.error("asyncComputed load failed:", err);
        }
        loading.value = false;
      });
  }

  const watchers = shouldReloadOnSchoolId
    ? [
        () => schoolStore.school_id,
        () => schoolStore.schoolDetails.data.integration,
        ...baseWatchers,
      ]
    : baseWatchers;

  for (const watcher of watchers) {
    watch(watcher, load);
  }

  load();
  return { data: value, loading, reload: load };
}